import Vue from 'vue';

/**
 * TODO: We can (and should) use helper functions,
 * rather than direct $emit for better type checking.
 */
export class UIEventBus {
  private static local = new Vue();
  private static isAppReady = false;
  public static isDebug = true;
  public static root: Pick<Vue, '$on' | '$off' | '$emit'> = new Vue();
  public static _view: Pick<Vue, '$on' | '$off' | '$emit'> = new Vue();

  /** Use provided root instance to send messages to Action Desk View */
  public static setActionDeskViewEventBus(view: Vue) {
    this._view = {
      $on: view.$on.bind(view),
      $off: view.$off.bind(view),
      $emit: (event: string, ...args: any[]) => {
        if (this.isDebug) {
          const date = new Date().toLocaleTimeString();
          console.debug('[Action Desk View Event Bus]', date, event, args);
        }

        return view.$emit(event, ...args);
      },
    };
  }

  /** Use provided root instance to send messages to neighboring cards */
  public static setRootEventBus(
    root: Vue,
    options?: { onRootEmit?: (event: string, params?: any) => void; },
  ) {
    const META_EVENTS = [
      ROOT_EVENTS.APP_READY,
      ROOT_EVENTS.APP_NOT_READY,
      ROOT_EVENTS.APP_PING,
    ];

    this.root = {
      $on: root.$on.bind(root),
      $off: root.$off.bind(root),
      $emit: (event: string, ...args: any[]) => {
        if (this.isDebug) {
          const date = new Date().toLocaleTimeString();
          console.debug('[Root Event Bus]', date, event, args);
        }

        /* If app is not ready notify user that feature events are not available */
        if (!META_EVENTS.includes(event) && !this.isAppReady) {
          return root.$emit(ROOT_EVENTS.APP_NOT_READY);
        }

        options?.onRootEmit?.(event, args[0]);

        return root.$emit(event, ...args);
      },
    };

    /* Ping feature allows cards to know whether they can send requests */
    this.root.$on(ROOT_EVENTS.APP_PING, this.onPing);
    this.root.$on(ROOT_EVENTS.APP_READY, this.onAppReady);
    this.root.$on(ROOT_EVENTS.APP_NOT_READY, this.onAppNotReady);
  }

  private static onPing() {
    UIEventBus.isAppReady
      ? UIEventBus.root.$emit(ROOT_EVENTS.APP_READY)
      : UIEventBus.root.$emit(ROOT_EVENTS.APP_NOT_READY);
  }

  private static onAppReady() {
    UIEventBus.isAppReady = true;
  }

  private static onAppNotReady() {
    UIEventBus.isAppReady = false;
  }

  public static cleanUp() {
    [this.local, this._view, this.root].map(i => i.$off());
  }

  public static $emit(event: string, ...args: any[]) {
    if (this.isDebug) {
      const date = new Date().toLocaleTimeString();
      console.debug('[Local Event Bus]', date, event, args);
    }
    return this.local.$emit(event, ...args);
  }

  public static $on(event: string, callback: (...args: any[]) => void) {
    this.local.$on(event, callback);
  }

  public static $off(event: string, callback: (...args: any[]) => void) {
    this.local.$off(event, callback);
  }
}

export const THROW_API_ERROR = 'THROW_API_ERROR';
export const THROW_WEBSOCKET_CONNECTION_ERROR = 'THROW_WEBSOCKET_CONNECTION_ERROR';
export const SHOW_NOTIFICATION_TOAST = 'SHOW_NOTIFICATION_TOAST';
export const CLEAR_NOTIFICATION_TOASTS = 'CLEAR_NOTIFICATION_TOASTS';
export const SELECT_SEARCH_RESULT_ITEM = 'SELECT_SEARCH_RESULT_ITEM';
export const CLICK_NOTIFICATION = 'CLICK_NOTIFICATION';
export const OPEN_DRAFTS_POPUP = 'OPEN_DRAFTS_POPUP';
export const HEADER_COMPONENT_OPEN = 'HEADER_COMPONENT_OPEN';
export const SESSIONS_LIST_SHOW_EXTRA_SESSIONS = 'SESSIONS_LIST_SHOW_EXTRA_SESSIONS';
export const SESSIONS_TIMELINE_TAGS_POPUP_TOGGLE = 'SESSIONS_TIMELINE_TAGS_POPUP_TOGGLE';
export const SESSIONS_TIMELINE_TRANSFER_TO_BOT_POPUP_TOGGLE = 'SESSIONS_TIMELINE_TRANSFER_TO_BOT_POPUP_TOGGLE';
export const SESSIONS_TIMELINE_ITEM_TOGGLE_SCROLL_START = 'SESSIONS_TIMELINE_ITEM_TOGGLE_SCROLL_START';
export const SESSIONS_TIMELINE_ITEM_TOGGLE_SCROLL_FINISH = 'SESSIONS_TIMELINE_ITEM_TOGGLE_SCROLL_FINISH';
export const SESSIONS_TIMELINE_ITEM_TOGGLE_FINISH = 'SESSIONS_TIMELINE_ITEM_TOGGLE_FINISH';
export const SESSIONS_TIMELINE_ITEM_SUMMARY_TOGGLE_BEFORE_START = 'SESSIONS_TIMELINE_ITEM_SUMMARY_TOGGLE_BEFORE_START';
export const SESSIONS_TIMELINE_ITEM_SUMMARY_TOGGLE_START = 'SESSIONS_TIMELINE_ITEM_SUMMARY_TOGGLE_START';
export const SESSIONS_TIMELINE_ITEM_SUMMARY_TOGGLE_FINISH = 'SESSIONS_TIMELINE_ITEM_SUMMARY_TOGGLE_FINISH';
export const SESSIONS_TIMELINE_SCROLL_TO_ACTIVE_CLICK = 'SESSIONS_TIMELINE_SCROLL_TO_ACTIVE_CLICK';
export const SESSIONS_TIMELINE_ACTIVE_SESSION_LOAD = 'SESSIONS_TIMELINE_ACTIVE_SESSION_LOAD';
export const SESSIONS_TIMELINE_ACTIVE_SESSION_CLAIM = 'SESSIONS_TIMELINE_ACTIVE_SESSION_CLAIM';
export const FAST_SEARCH_RESET = 'FAST_SEARCH_RESET';
export const LOCAL_SETTINGS_UPDATE = 'LOCAL_SETTINGS_UPDATE';
export const SELECT_ADD_CARD_COMPONENT_ITEM = 'SELECT_ADD_CARD_COMPONENT_ITEM';
export const OPEN_INIT_CONVERSATION_POPUP = 'OPEN_INIT_CONVERSATION_POPUP';
export const APP_TIMELINE_SESSION_VISIBILITY_CHANGE = 'APP_TIMELINE_SESSION_VISIBILITY_CHANGE';
export const DIALER_ADD_MEMBER_TO_CALL_OPTION_CLICK = 'DIALER_ADD_MEMBER_TO_CALL_OPTION_CLICK';
export const LAYOUT_BUILDER_SAVE_CLICK = 'LAYOUT_BUILDER_SAVE_CLICK';
export const LAYOUT_BUILDER_DISCARD_CLICK = 'LAYOUT_BUILDER_DISCARD_CLICK';
export const LAYOUT_BUILDER_RESET_CLICK = 'LAYOUT_BUILDER_RESET_CLICK';
export const CHATBOX_SET_COMMAND = 'CHATBOX_SET_COMMAND';
export const RELOAD_TIMELINE = 'RELOAD_TIMELINE';
export const SESSION_UPSERT = 'SESSION_UPSERT';
export const CONTACTS_MERGE = 'CONTACTS_MERGE';

export const ROOT_EVENTS = {
  APP_PING: 'HITL::app-ping',
  APP_READY: 'HITL::app-ready',
  APP_NOT_READY: 'HITL::app-not-ready',

  CARD_READY: 'HITL::card-ready',
  CURRENT_STATE: 'HITL::current-state',

  SELECTED_CONTACT_UPDATE: 'HITL::selected-contact-update',
  SELECTED_SESSION_UPDATE: 'HITL::selected-session-update',

  AGENT_STATUS_CHANGE: 'HITL::agent-status-change',
  SESSION_EVENTS_ADD: 'HITL::session-events-add',
  SESSION_CLOSED: 'HITL::session-closed',
  SESSION_HIDDEN: 'HITL::session-hidden',
  SESSION_UPSERTED: 'HITL::session-upserted',

  GET_SELECTED_CONTACT: 'HITL::get-selected-contact',
  SELECTED_CONTACT: 'HITL::selected-contact',
  GET_SELECTED_SESSION: 'HITL::get-selected-session',
  SELECTED_SESSION: 'HITL::selected-session',

  SELECT_SESSION: 'HITL::select-session',
  SELECT_SESSION_SUCCESS: 'HITL::select-session-success',
  CLAIM_SESSION: 'HITL::claim-session',
  CLAIM_SESSION_SUCCESS: 'HITL::claim-session-success',
  REJECT_CLAIM: 'HITL::reject-claim',
  REJECT_CLAIM_SUCCESS: 'HITL::reject-claim-success',
  CLOSE_SESSION: 'HITL::close-session',
  CLOSE_SESSION_SUCCESS: 'HITL::close-session-success',

  SELECT_CONTACT: 'HITL::select-contact',
  SELECT_CONTACT_SUCCESS: 'HITL::select-contact-success',

  CHANGE_ACTIVE_SESSION_CONTACT: 'HITL::change-active-session-contact',
  CHANGE_ACTIVE_SESSION_CONTACT_SUCCESS: 'HITL::change-active-session-contact-success',

  SEND_MESSAGE: 'HITL::send-message',
  SEND_MESSAGE_SUCCESS: 'HITL::send-message-success',
  SEND_COMMAND: 'HITL::send-command',
  SEND_COMMAND_SUCCESS: 'HITL::send-command-success',
  SEND_WS_MESSAGE: 'HITL::send-ws-message',
  SEND_WS_MESSAGE_SUCCESS: 'HITL::send-ws-message-success',

  START_CONVERSATION: 'HITL::start-conversation',
  START_CONVERSATION_SUCCESS: 'HITL::start-conversation-success',
  SET_CHATBOX_CONTENT: 'HITL::set-chatbox-content',
  ADD_CHATBOX_MEDIA: 'HITL::add-chatbox-media',

  CHANGE_WORKSPACE: 'HITL::change-workspace',
  /** Mobile layout & micro embed — switch sessions / timeline / contact info / card page (payload shape varies by shell). */
  SET_ACTIVE_APP_PAGE: 'HITL::set-active-app-page',

  HEADER_COMPONENT_CLOSE: 'HITL::header-component-close',
};
